import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  ServiceResponse,
  ServicesListResponse,
  ServiceListQueryParams,
  IServiceData,
  SubCategoryResponse,
  ServiceLanguage,
} from '@/types/service-management.types';

export const ServiceManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getServices: builder.query<
      PaginatedApiResponse<IServiceData[]>,
      ServiceListQueryParams
    >({
      query: (params) => ({
        url: '/service-providers/services',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ServicesListResponse) => {
        const totalItems =
          response.totalItems ?? response.total ?? response.data?.length ?? 0;
        const pageLimit = response.limit ?? undefined;
        const currentPage = response.currentPage ?? response.page ?? 1;
        const totalPages =
          response.totalPages ??
          (pageLimit ? Math.ceil(totalItems / pageLimit) : undefined);

        return {
          data: response.data,
          totalItems,
          totalPages,
          currentPage,
          status: response.status,
          message: response.message,
        } as PaginatedApiResponse<IServiceData[]>;
      },
      providesTags: ['Services'],
      keepUnusedDataFor: 0,
    }),

    getServiceById: builder.query<ServiceResponse, string>({
      query: (serviceId) => ({
        url: `/service-providers/services/${serviceId}`,
        method: 'GET',
      }),
      transformResponse: (response: ServiceResponse) => response,
      providesTags: (result, error, serviceId) => [
        { type: 'Services', id: serviceId },
      ],
      keepUnusedDataFor: 0,
    }),

    createService: builder.mutation<ApiResponse<IServiceData>, FormData>({
      query: (data) => ({
        url: '/service-providers/services',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
      transformResponse: (response: ApiResponse<IServiceData>) => response,
      invalidatesTags: ['Services'],
    }),

    updateService: builder.mutation<
      ApiResponse<IServiceData>,
      { serviceId: string; data: FormData }
    >({
      query: ({ serviceId, data }) => ({
        url: `/service-providers/${serviceId}/services`,
        method: 'PUT',
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
      transformResponse: (response: ApiResponse<IServiceData>) => response,
      invalidatesTags: ['Services'],
    }),

    deleteService: builder.mutation<ApiResponse<void>, string>({
      query: (serviceId) => ({
        url: `/service-providers/${serviceId}/services`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['Services'],
    }),

    // Toggle a service's public visibility (harbor-scoped)
    toggleServiceVisibility: builder.mutation<
      ApiResponse<IServiceData>,
      { serviceId: string; is_visible: boolean }
    >({
      query: ({ serviceId, is_visible }) => ({
        url: `/service-providers/services/${serviceId}/visibility`,
        method: 'PATCH',
        data: { is_visible },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: ApiResponse<IServiceData>) => response,
      invalidatesTags: ['Services'],
    }),

    getSubCategories: builder.query<
      SubCategoryResponse,
      { category: string; language?: ServiceLanguage }
    >({
      query: ({ category, language = ServiceLanguage.EN }) => ({
        url: '/service-providers/services-sub-category',
        method: 'GET',
        params: { category },
        headers: {
          'x-lang': language,
        },
      }),
      transformResponse: (response: SubCategoryResponse) => response,
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    translateService: builder.mutation<
      {
        status: boolean;
        message: string;
        translations: Record<string, { name?: string; description?: string }>;
        warning_message?: string;
      },
      {
        service_id?: string;
        name?: string;
        description: string;
        source_language: string;
      }
    >({
      query: (data) => ({
        url: '/service-providers/translate',
        method: 'POST',
        data,
      }),
    }),

    submitService: builder.mutation<ApiResponse<void>, string>({
      query: (serviceId) => ({
        url: `/service-providers/services/${serviceId}/submit`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, serviceId) => [
        { type: 'Services', id: serviceId },
        'Services',
      ],
    }),

    withdrawService: builder.mutation<ApiResponse<void>, string>({
      query: (serviceId) => ({
        url: `/service-providers/services/${serviceId}/withdraw`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, serviceId) => [
        { type: 'Services', id: serviceId },
        'Services',
      ],
    }),
  }),
});

export const {
  useGetServicesQuery,
  useGetServiceByIdQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useGetSubCategoriesQuery,
  useLazyGetSubCategoriesQuery,
  useTranslateServiceMutation,
  useToggleServiceVisibilityMutation,
  useSubmitServiceMutation,
  useWithdrawServiceMutation,
} = ServiceManagementService;
